import { useEffect, useState, type CSSProperties } from "react";
import type {
  DoorSignOrientation,
  DoorSignProjection,
  PremiseConfig,
} from "@shared/types/foyer";
import { doorSignPalette, type DoorSignPalette } from "./palette";
import { presenceLabel, roomKicker, statusLabel } from "./labels";
import { useDoorSign } from "./useDoorSign";

/**
 * Corridor door sign - a glanceable, full-screen nameplate mounted OUTSIDE a
 * resident's room. Renders only the PHI-light projection: room, presence, a
 * staff status, and a name already reduced to the premise's identity policy
 * server-side (often ""). See docs/door-sign-spec.md §3.
 *
 * The same renderer drives both mount orientations (a layout toggle, not a
 * separate build): "landscape" matches the hospitality panel we ship; "portrait"
 * suits a tall frame beside a doorway - presence drops below the room number and
 * the footer left-aligns. Orientation is per-device (`PremiseScreen`).
 */

function formatClock(now: Date, locale?: string): string {
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(now);
  } catch {
    return "";
  }
}

interface ViewProps {
  palette: DoorSignPalette;
  roomLabel: string;
  premiseName?: string;
  locale?: string;
  projection: DoorSignProjection | null;
  now: Date;
  /** Mount orientation. Drives the layout; defaults to "landscape". */
  orientation?: DoorSignOrientation;
}

/**
 * Pure presentational layer (exported for tests). Decides what's shown from the
 * projection alone: a name only when occupied AND the policy left one; a status
 * pill only when set; presence always. The `orientation` prop selects between
 * the landscape and portrait layouts - same content, same testids, different
 * arrangement.
 */
export function DoorSignView({
  palette: p,
  roomLabel,
  premiseName,
  locale,
  projection,
  now,
  orientation = "landscape",
}: ViewProps) {
  const portrait = orientation === "portrait";
  const occupied = Boolean(projection?.occupied);
  const name = occupied ? (projection?.displayName ?? "").trim() : "";
  const status = occupied ? (projection?.status ?? "none") : "none";
  const statusText = statusLabel(status, locale);

  const root: CSSProperties = {
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    background: p.paper,
    color: p.ink,
    fontFamily: p.sans,
    display: "flex",
    flexDirection: "column",
    padding: "clamp(24px, 5vw, 80px)",
    boxSizing: "border-box",
    overflow: "hidden",
  };

  // Room number is the dominant element. In portrait the viewport is narrow, so
  // scale it off the smaller axis (vmin) to keep it from overflowing the width;
  // landscape can lean on vw for the big, wide numeral.
  const kicker = (
    <div
      style={{
        fontFamily: p.mono,
        color: p.inkMuted,
        letterSpacing: "0.28em",
        fontSize: "clamp(14px, 1.8vw, 28px)",
        fontWeight: 600,
      }}
    >
      {roomKicker(locale)}
    </div>
  );

  const presence = (
    <div
      data-testid="door-sign-presence"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.6em",
        fontSize: portrait
          ? "clamp(18px, 3.6vw, 40px)"
          : "clamp(14px, 1.7vw, 26px)",
        fontWeight: 600,
        color: occupied ? p.gDeep : p.inkMuted,
      }}
    >
      <span
        style={{
          width: "0.7em",
          height: "0.7em",
          borderRadius: "50%",
          background: occupied ? p.gCore : "transparent",
          border: occupied ? "none" : `2px solid ${p.inkMuted}`,
        }}
      />
      {presenceLabel(occupied, locale)}
    </div>
  );

  const room = (
    <div
      data-testid="door-sign-room"
      style={{
        marginTop: "clamp(8px, 1.5vh, 24px)",
        fontWeight: 800,
        lineHeight: 0.95,
        letterSpacing: "-0.02em",
        fontSize: portrait
          ? "clamp(96px, 30vmin, 280px)"
          : "clamp(72px, 16vw, 320px)",
      }}
    >
      {roomLabel}
    </div>
  );

  const identity = name ? (
    <div
      data-testid="door-sign-name"
      style={{
        marginTop: "clamp(8px, 2vh, 28px)",
        fontWeight: 600,
        color: p.gDeep,
        fontSize: portrait
          ? "clamp(36px, 9vw, 96px)"
          : "clamp(32px, 6vw, 120px)",
        lineHeight: 1.05,
      }}
    >
      {name}
    </div>
  ) : null;

  const statusPill = statusText ? (
    <div
      data-testid="door-sign-status"
      style={{
        marginTop: "clamp(16px, 3vh, 44px)",
        alignSelf: "flex-start",
        background: p.gTint,
        color: p.gDeep,
        borderRadius: 999,
        padding: "0.5em 1.1em",
        fontSize: portrait
          ? "clamp(24px, 5vw, 52px)"
          : "clamp(22px, 3.4vw, 64px)",
        fontWeight: 600,
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      {statusText}
    </div>
  ) : null;

  const footer = (
    <div
      style={{
        display: "flex",
        // Portrait stacks left-aligned (sketch §3); landscape keeps it quiet in
        // the bottom-right corner.
        justifyContent: portrait ? "flex-start" : "flex-end",
        alignItems: "baseline",
        gap: "0.6em",
        color: p.inkSoft,
        fontSize: "clamp(16px, 1.8vw, 30px)",
      }}
    >
      {premiseName ? <span>{premiseName}</span> : null}
      {premiseName ? <span style={{ color: p.hair }}>·</span> : null}
      <span style={{ fontFamily: p.mono }}>{formatClock(now, locale)}</span>
    </div>
  );

  return (
    <div
      style={root}
      data-testid="door-sign"
      data-occupied={occupied}
      data-orientation={orientation}
    >
      {portrait ? (
        // Portrait: kicker → room → presence (stacked) → identity → status.
        <>
          {kicker}
          {room}
          <div style={{ marginTop: "clamp(16px, 3vh, 40px)" }}>{presence}</div>
          {identity}
          {statusPill}
        </>
      ) : (
        // Landscape: kicker + presence share the top row; room dominates below.
        <>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            {kicker}
            {presence}
          </div>
          {room}
          {identity}
          {statusPill}
        </>
      )}

      <div style={{ flex: 1 }} />

      {footer}
    </div>
  );
}

interface SurfaceProps {
  config: Pick<PremiseConfig, "scheme" | "premiseName" | "defaultLocale"> & {
    rooms?: { id: string; name?: string }[];
  };
  roomId: string;
  /** Mount orientation for this device. Defaults to "landscape". */
  orientation?: DoorSignOrientation;
  /** Design/CMS preview: skip the authenticated fetch + use previewProjection. */
  preview?: boolean;
  previewProjection?: DoorSignProjection;
}

/**
 * Container: resolves the palette + room label from the premise config, runs the
 * polling hook (unless previewing), ticks a clock, and renders the view.
 */
export default function DoorSignSurface({
  config,
  roomId,
  orientation,
  preview,
  previewProjection,
}: SurfaceProps) {
  const { projection: fetched } = useDoorSign(!preview);
  const projection = previewProjection ?? fetched;

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(t);
  }, []);

  const palette = doorSignPalette(config);
  // The projection names the resident's room the sign serves; the screen's
  // own room is a door-sign row of its own (Sigge, 2026-10-19), so label by
  // the served room once the projection says which.
  const labelRoomId = projection?.roomId || roomId;
  const room = config.rooms?.find((r) => r.id === labelRoomId);
  const roomLabel = room?.name?.trim() || labelRoomId;

  return (
    <DoorSignView
      palette={palette}
      roomLabel={roomLabel}
      premiseName={config.premiseName}
      locale={config.defaultLocale}
      projection={projection}
      now={now}
      orientation={orientation}
    />
  );
}
